import { loadCatalogConfigs, updateCatalogConfig } from 'js/actions/catalogConfigs';
import { Integration, integrationTypes } from 'js/constants/externalIntegrations';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import IntegrationList from 'src/new/components/settings/Integrations/IntegrationList';
import { find } from 'lodash';
import { useModals } from 'new/hooks/useModals';
import { DeactivateIntegration } from 'new/components/settings/Integrations/IntegrationList/DeactivateIntegration';
import { CatalogConfig } from '@nexla/@types';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

const title = 'Integrate Nexla with popular Enterprise Data Catalogs';
const description =
  "Enrich your enterprise data catalog by registering your organization's Nexsets in your catalog of choice. Once an organization administrator links and activates a catalog below all members in the organization will be able to register Nexsets in the active catalog.";

/**
 * Retrieves the appropriate catalog configuration based on the provided configurations.
 * Active configurations are high priority over inactive ones.
 * If all configurations are inactive, the latest one is chosen.
 */
function getCatalogConfig(catalogConfigs: Record<number, CatalogConfig>) {
  const list = Object.values(catalogConfigs);
  /**
   * case I: pick the one that is active.
   * case II: if all are inactive pick the latest one.
   */
  return list.sort((a, b) => {
    if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
    if (b.status === 'ACTIVE' && a.status !== 'ACTIVE') return 1;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB.getTime() - dateA.getTime();
  });
}

export const DatasetCatalog = () => {
  const { openModal } = useModals();
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const {
    entities: { catalogConfigs, credentials },
  } = useSelector((state: any) => state);

  const allCatalogConfigs = getCatalogConfig(catalogConfigs);

  useEffect(() => {
    dispatch(loadCatalogConfigs());
  }, []);

  const onActivationChange = (integration: Integration, catalogConfig: any) => (_, checked) => {
    if (!catalogConfig) {
      return;
    }

    // if another integration is active, pause it first
    const activeIntegration =
      integration.meta.type === integrationTypes.data_catalog &&
      find(
        catalogConfigs,
        (catalogConfig) =>
          catalogConfig.name !== integration.meta.name && catalogConfig.status === 'ACTIVE',
      );

    if (checked) {
      if (activeIntegration) {
        // ask for confirmation to pause the currently active one
        openModal(DeactivateIntegration.modalConfig.name, {
          activeIntegration,
          currentIntegration: catalogConfig,
        });
      } else {
        dispatch(
          updateCatalogConfig(catalogConfig.id, {
            status: 'ACTIVE',
          }),
        );
      }
    } else {
      dispatch(
        updateCatalogConfig(catalogConfig.id, {
          status: 'PAUSED',
        }),
      );
    }
  };

  const itemAdapter = (integration: Integration) => {
    const catalogConfig =
      integration.meta.type === integrationTypes.data_catalog &&
      Object.values(allCatalogConfigs).find((config) => {
        {
          const credential = credentials[config?.data_credentials];
          return credential?.connector?.type === integration.credential.type;
        }
      });

    const isActive = catalogConfig?.status === 'ACTIVE';

    return (
      <IntegrationList.Item
        isActive={isActive}
        onActivationChange={onActivationChange(integration, catalogConfig)}
      />
    );
  };

  return (
    <IntegrationList
      integrationType={integrationTypes.data_catalog}
      title={title}
      itemAdapter={itemAdapter}
      description={description}
    />
  );
};
